import { writable } from 'svelte/store';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: ToastAction;
}

const { subscribe, update } = writable<Toast[]>([]);

// A toast with an action needs time to read, aim and click.
const DEFAULT_DURATION = 3000;
const DEFAULT_DURATION_WITH_ACTION = 8000;

function addToast(
  message: string,
  type: Toast['type'] = 'info',
  duration?: number,
  options?: ToastOptions,
): void {
  const id = Math.random().toString(36).slice(2, 9);
  const action = options?.action;
  duration ??= action ? DEFAULT_DURATION_WITH_ACTION : DEFAULT_DURATION;
  const toast: Toast = action
    ? { id, message, type, duration, action }
    : { id, message, type, duration };

  update((toasts) => [...toasts, toast]);

  if (duration > 0) {
    timers.set(id, { remaining: duration, startedAt: 0, handle: undefined });
    startTimer(id);
  }
}

// Timers live in the browser tab only (UI state, not shared across instances).
interface TimerState {
  remaining: number;
  startedAt: number;
  handle: ReturnType<typeof setTimeout> | undefined;
}
const timers = new Map<string, TimerState>();

function startTimer(id: string): void {
  const timer = timers.get(id);
  if (!timer || timer.handle !== undefined) return;
  timer.startedAt = Date.now();
  timer.handle = setTimeout(() => removeToast(id), timer.remaining);
}

/** Stops the countdown (hover/focus); the remaining visible time is kept. */
function pauseToast(id: string): void {
  const timer = timers.get(id);
  if (!timer || timer.handle === undefined) return;
  clearTimeout(timer.handle);
  timer.handle = undefined;
  timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
}

function resumeToast(id: string): void {
  startTimer(id);
}

function removeToast(id: string): void {
  const timer = timers.get(id);
  if (timer?.handle !== undefined) clearTimeout(timer.handle);
  timers.delete(id);
  update((toasts) => toasts.filter((t) => t.id !== id));
}

export const toastStore = {
  subscribe,
  success: (message: string, duration?: number, options?: ToastOptions) =>
    addToast(message, 'success', duration, options),
  error: (message: string, duration?: number) => addToast(message, 'error', duration),
  warning: (message: string, duration?: number) => addToast(message, 'warning', duration),
  info: (message: string, duration?: number) => addToast(message, 'info', duration),
  remove: removeToast,
  pause: pauseToast,
  resume: resumeToast,
};
